import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faNetworkWired,
  faShieldHalved,
  faFingerprint,
  faMagnifyingGlass,
  faBolt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

export default function SecurityArchitecture() {
  const [selectedLayer, setSelectedLayer] = useState("siem");

  const architectureLayers = [
    {
      id: "endpoints",
      title: "1. Endpoints & Infrastructure",
      subtitle: "Workstations, Domain Controllers, Linux Servers & Firewalls",
      icon: faServer,
      color: "#38BDF8",
      details:
        "Telemetry source points. Every endpoint runs hardened configurations and Wazuh agent daemons, gathering audit logs, rootcheck telemetry, file integrity events, and operating system metrics.",
      bullets: [
        "Windows Server & Workstation Event Channels (Sysmon, Security, System)",
        "Linux auth, syslog, auditd and kernel logs",
        "Edge firewalls, switches, and network gateways streaming via Syslog",
      ],
    },
    {
      id: "collection",
      title: "2. Log Collection & Secure Transport",
      subtitle: "TLS-Encrypted Message Ingestion & Normalization",
      icon: faNetworkWired,
      color: "#818CF8",
      details:
        "Raw events are captured in real-time and forwarded over authenticated TLS channels to prevent eavesdropping and data tampering in transit.",
      bullets: [
        "Authenticated Wazuh Agent protocol over TCP port 1514",
        "Centralized Syslog-ng / Rsyslog listeners for network appliances",
        "Local agent buffering during intermittent network disconnects",
      ],
    },
    {
      id: "siem",
      title: "3. Wazuh SIEM Analysis Engine",
      subtitle: "Decoders, Custom Rulesets & Correlation",
      icon: faShieldHalved,
      color: "#38BDF8",
      details:
        "The core analytical brain. Ingested events pass through specialized decoders that extract normalized key-value fields, then evaluate against parent-child rule hierarchies and custom organization-specific detections.",
      bullets: [
        "Field extraction and JSON parsing across heterogeneous logs",
        "Rule categorization with severity rating (Levels 0–16)",
        "Correlation of multi-event brute-force and privilege escalation chains",
      ],
    },
    {
      id: "analysis",
      title: "4. Detection & Investigation",
      subtitle: "Dual-Track Threat Validation & Deep Index Forensics",
      icon: faFingerprint,
      color: "#F59E0B",
      details:
        "Detections trigger structured alerts while all normalized event indices remain indexed in the Wazuh Indexer for fast ad-hoc investigative querying.",
      bullets: [
        "Detection: High-fidelity alert notifications mapped to MITRE ATT&CK",
        "Investigation: Hot/Warm indexed log queries across historical intervals",
        "Contextual triage eliminating false positives before escalation",
      ],
    },
    {
      id: "response",
      title: "5. Remediation & Incident Response",
      subtitle: "Containment, Root-Cause Analysis & Hardening Retest",
      icon: faBolt,
      color: "#22C55E",
      details:
        "Actionable security conclusions. Turning investigation findings into concrete defensive actions: host isolation, GPO policy corrections, and firewall rule updates.",
      bullets: [
        "Targeted remediation guidance for sysadmins and engineering teams",
        "Active Response triggers for high-confidence brute-force mitigation",
        "Closed-loop verification through configuration re-auditing",
      ],
    },
  ];

  const currentLayerData =
    architectureLayers.find((layer) => layer.id === selectedLayer) || architectureLayers[2];

  return (
    <section id="architecture" className="cyber-section">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-5">
          <div className="section-label">DEFENSE ARCHITECTURE</div>
          <h2 className="section-title">How I Think About Security</h2>
          <p className="section-subtitle mx-auto">
            Practical defense is not a series of isolated tools. It is an end-to-end telemetry,
            detection, and response architecture built to provide clear visibility and rapid containment.
          </p>
        </div>

        <div className="row g-4 align-items-center">
          {/* Left: Diagram View */}
          <div className="col-lg-6">
            <div
              className="cyber-card p-4"
              style={{
                background: "linear-gradient(180deg, #0E1116 0%, #080A0D 100%)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <div className="d-flex flex-column align-items-center gap-3">
                {/* 1. Endpoints */}
                <button
                  type="button"
                  onClick={() => setSelectedLayer("endpoints")}
                  className={`w-100 text-start arch-node btn p-3 text-white ${
                    selectedLayer === "endpoints" ? "arch-node-active" : ""
                  }`}
                  style={{
                    background: selectedLayer === "endpoints" ? "rgba(56, 189, 248, 0.12)" : "var(--bg-surface-elevated)",
                    border: `1px solid ${selectedLayer === "endpoints" ? "var(--accent)" : "var(--border-subtle)"}`,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FontAwesomeIcon icon={faServer} style={{ color: "#38BDF8" }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>ENDPOINTS &amp; HOSTS</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                          Workstations • Linux • Windows • Firewalls
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-muted" style={{ fontSize: "0.75rem" }}>01</span>
                  </div>
                </button>

                {/* Arrow */}
                <div className="d-flex flex-column align-items-center">
                  <div style={{ width: "2px", height: "16px", background: "var(--border-accent)" }} />
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid var(--accent)",
                    }}
                  />
                </div>

                {/* 2. Log Collection */}
                <button
                  type="button"
                  onClick={() => setSelectedLayer("collection")}
                  className={`w-100 text-start arch-node btn p-3 text-white ${
                    selectedLayer === "collection" ? "arch-node-active" : ""
                  }`}
                  style={{
                    background: selectedLayer === "collection" ? "rgba(129, 140, 248, 0.12)" : "var(--bg-surface-elevated)",
                    border: `1px solid ${selectedLayer === "collection" ? "#818CF8" : "var(--border-subtle)"}`,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FontAwesomeIcon icon={faNetworkWired} style={{ color: "#818CF8" }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>LOG COLLECTION &amp; TRANSPORT</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                          Encrypted Agent Stream &amp; Syslog Ingestion
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-muted" style={{ fontSize: "0.75rem" }}>02</span>
                  </div>
                </button>

                {/* Arrow */}
                <div className="d-flex flex-column align-items-center">
                  <div style={{ width: "2px", height: "16px", background: "var(--border-accent)" }} />
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid var(--accent)",
                    }}
                  />
                </div>

                {/* 3. Wazuh SIEM */}
                <button
                  type="button"
                  onClick={() => setSelectedLayer("siem")}
                  className={`w-100 text-start arch-node btn p-3 text-white ${
                    selectedLayer === "siem" ? "arch-node-active" : ""
                  }`}
                  style={{
                    background: selectedLayer === "siem" ? "rgba(56, 189, 248, 0.18)" : "var(--bg-surface-elevated)",
                    border: `1px solid ${selectedLayer === "siem" ? "var(--accent)" : "rgba(56, 189, 248, 0.3)"}`,
                    boxShadow: selectedLayer === "siem" ? "0 0 20px var(--accent-glow)" : "none",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FontAwesomeIcon icon={faShieldHalved} style={{ color: "var(--accent)" }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--text-primary)" }}>
                          WAZUH SIEM
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                          Decoders • Correlation Engine • Rulesets
                        </div>
                      </div>
                    </div>
                    <span className="font-mono" style={{ fontSize: "0.75rem", color: "var(--accent)" }}>CORE</span>
                  </div>
                </button>

                {/* Branch split */}
                <div className="w-100 position-relative my-1">
                  <div style={{ width: "2px", height: "14px", background: "var(--border-accent)", margin: "0 auto" }} />
                  <div style={{ height: "2px", background: "var(--border-accent)", width: "70%", margin: "0 auto" }} />
                  <div className="d-flex justify-content-between" style={{ width: "70%", margin: "0 auto" }}>
                    <div style={{ width: "2px", height: "12px", background: "var(--border-accent)" }} />
                    <div style={{ width: "2px", height: "12px", background: "var(--border-accent)" }} />
                  </div>
                </div>

                {/* 4. Split: Detection & Investigation */}
                <div className="row g-3 w-100">
                  <div className="col-6">
                    <button
                      type="button"
                      onClick={() => setSelectedLayer("analysis")}
                      className={`w-100 text-center btn p-2 text-white cyber-card-elevated ${
                        selectedLayer === "analysis" ? "arch-node-active" : ""
                      }`}
                      style={{
                        border: `1px solid ${selectedLayer === "analysis" ? "var(--warning)" : "rgba(245, 158, 11, 0.3)"}`,
                      }}
                    >
                      <FontAwesomeIcon icon={faFingerprint} style={{ color: "var(--warning)" }} />
                      <div style={{ fontWeight: 600, fontSize: "0.85rem", marginTop: 4 }}>DETECTION</div>
                      <div className="text-muted font-mono" style={{ fontSize: "0.68rem" }}>Rule Match</div>
                    </button>
                  </div>
                  <div className="col-6">
                    <button
                      type="button"
                      onClick={() => setSelectedLayer("analysis")}
                      className={`w-100 text-center btn p-2 text-white cyber-card-elevated ${
                        selectedLayer === "analysis" ? "arch-node-active" : ""
                      }`}
                      style={{
                        border: `1px solid ${selectedLayer === "analysis" ? "var(--accent)" : "rgba(56, 189, 248, 0.3)"}`,
                      }}
                    >
                      <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "var(--accent)" }} />
                      <div style={{ fontWeight: 600, fontSize: "0.85rem", marginTop: 4 }}>INVESTIGATION</div>
                      <div className="text-muted font-mono" style={{ fontSize: "0.68rem" }}>Log Query</div>
                    </button>
                  </div>
                </div>

                {/* Convergence Arrow */}
                <div className="d-flex flex-column align-items-center">
                  <div style={{ width: "2px", height: "16px", background: "var(--border-accent)" }} />
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid var(--success)",
                    }}
                  />
                </div>

                {/* 5. Response */}
                <button
                  type="button"
                  onClick={() => setSelectedLayer("response")}
                  className={`w-100 text-start arch-node btn p-3 text-white ${
                    selectedLayer === "response" ? "arch-node-active" : ""
                  }`}
                  style={{
                    background: selectedLayer === "response" ? "rgba(34, 197, 94, 0.15)" : "var(--bg-surface-elevated)",
                    border: `1px solid ${selectedLayer === "response" ? "var(--success)" : "rgba(34, 197, 94, 0.3)"}`,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                      <FontAwesomeIcon icon={faBolt} style={{ color: "var(--success)" }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text-primary)" }}>
                          INCIDENT RESPONSE &amp; REMEDIATION
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                          Containment • Policy Hardening • Verification
                        </div>
                      </div>
                    </div>
                    <span className="font-mono text-success" style={{ fontSize: "0.75rem" }}>05</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right: Detailed Deep Dive Pane */}
          <div className="col-lg-6">
            <div
              className="cyber-card p-4 h-100 d-flex flex-column justify-content-between"
              style={{
                background: "var(--bg-surface)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="section-label mb-0">LAYER INSPECTOR</span>
                  <span className="font-mono text-muted" style={{ fontSize: "0.75rem" }}>
                    Click any layer to inspect
                  </span>
                </div>

                <div className="d-flex align-items-center gap-3 mb-3">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      background: "rgba(56, 189, 248, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: currentLayerData.color,
                      fontSize: "1.2rem",
                    }}
                  >
                    <FontAwesomeIcon icon={currentLayerData.icon} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: "1.3rem", fontWeight: 700, margin: 0 }}>
                      {currentLayerData.title}
                    </h3>
                    <div className="font-mono" style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      {currentLayerData.subtitle}
                    </div>
                  </div>
                </div>

                <p style={{ color: "var(--text-primary)", fontSize: "0.95rem", lineHeight: 1.7, marginBottom: 20 }}>
                  {currentLayerData.details}
                </p>

                <div className="mb-4">
                  <div
                    className="font-mono text-muted mb-2 text-uppercase"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.08em" }}
                  >
                    Technical Specifications
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {currentLayerData.bullets.map((bullet, idx) => (
                      <div key={idx} className="d-flex align-items-start gap-2">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ color: "var(--accent)", marginTop: "4px", fontSize: "0.85rem" }}
                        />
                        <span style={{ fontSize: "0.88rem", color: "var(--text-secondary)" }}>
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className="p-3 rounded-2"
                style={{
                  background: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span className="font-mono text-muted" style={{ fontSize: "0.75rem" }}>
                    ENGINEERING PHILOSOPHY
                  </span>
                  <span className="badge font-mono" style={{ background: "rgba(56, 189, 248, 0.1)", color: "var(--accent)" }}>
                    DEFENSE-IN-DEPTH
                  </span>
                </div>
                <p className="mt-2 mb-0" style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                  Zero trust starts with high-fidelity visibility. Security alerts must provide sufficient forensic context to allow an analyst to discern benign behavior from adversary action in seconds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

